/**
 * SVG path data, read properly.
 *
 * The shapes this project draws itself are written in a deliberately small
 * subset - M, L, C, Z, absolute - because that is all a library shape needs and
 * all the press sinks have to understand. A drawing made in Illustrator is not
 * so polite: it arrives with relative commands, shorthand curves, horizontal
 * and vertical lines, quadratics and elliptical arcs, several subpaths in one
 * string, and a transform on every group above it.
 *
 * So this reads all of that once, on the way in, and gives back the same small
 * subset. Everything downstream - the canvas, the proof, the press file - keeps
 * reading what it already read.
 *
 * Arcs become cubics, at up to a quarter turn each, which is accurate to about
 * a thousandth of the radius: far finer than any press, and the only honest way
 * to carry an arc through a pipeline that has no arcs in it.
 */

export interface Point {
  x: number;
  y: number;
}

/** One cubic segment, from wherever the path currently is. */
export interface Segment {
  c1: Point;
  c2: Point;
  to: Point;
}

export interface SubPath {
  start: Point;
  segments: Segment[];
  closed: boolean;
}

/** a b c d e f, as SVG writes a matrix. */
export type Matrix = [ number, number, number, number, number, number ];

export const IDENTITY: Matrix = [ 1, 0, 0, 1, 0, 0 ];

/** Two matrices, applied left then right - the order SVG nests transforms in. */
export function multiply( m: Matrix, n: Matrix ): Matrix {
  return [
    m[ 0 ] * n[ 0 ] + m[ 2 ] * n[ 1 ],
    m[ 1 ] * n[ 0 ] + m[ 3 ] * n[ 1 ],
    m[ 0 ] * n[ 2 ] + m[ 2 ] * n[ 3 ],
    m[ 1 ] * n[ 2 ] + m[ 3 ] * n[ 3 ],
    m[ 0 ] * n[ 4 ] + m[ 2 ] * n[ 5 ] + m[ 4 ],
    m[ 1 ] * n[ 4 ] + m[ 3 ] * n[ 5 ] + m[ 5 ],
  ];
}

export function apply( m: Matrix, point: Point ): Point {
  return {
    x: m[ 0 ] * point.x + m[ 2 ] * point.y + m[ 4 ],
    y: m[ 1 ] * point.x + m[ 3 ] * point.y + m[ 5 ],
  };
}

/**
 * An SVG transform attribute - matrix, translate, scale, rotate, skewX, skewY -
 * as one matrix. Anything unrecognised is ignored rather than guessed at.
 */
export function parseTransform( value: string | null ): Matrix {
  if ( ! value ) return IDENTITY;

  let out: Matrix = IDENTITY;

  for ( const part of value.matchAll( /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g ) ) {
    const n = ( part[ 2 ] ?? '' ).split( /[\s,]+/ ).filter( Boolean ).map( Number );
    const rad = ( degrees: number ) => ( degrees * Math.PI ) / 180;

    let step: Matrix = IDENTITY;

    switch ( part[ 1 ] ) {
      case 'matrix':
        if ( n.length >= 6 ) step = [ n[ 0 ]!, n[ 1 ]!, n[ 2 ]!, n[ 3 ]!, n[ 4 ]!, n[ 5 ]! ];
        break;

      case 'translate':
        step = [ 1, 0, 0, 1, n[ 0 ] ?? 0, n[ 1 ] ?? 0 ];
        break;

      case 'scale':
        step = [ n[ 0 ] ?? 1, 0, 0, n[ 1 ] ?? n[ 0 ] ?? 1, 0, 0 ];
        break;

      case 'rotate': {
        const angle = rad( n[ 0 ] ?? 0 );
        const turn: Matrix = [ Math.cos( angle ), Math.sin( angle ), -Math.sin( angle ), Math.cos( angle ), 0, 0 ];

        // rotate(a cx cy) turns about a point rather than the origin.
        step = n.length >= 3
          ? multiply( multiply( [ 1, 0, 0, 1, n[ 1 ]!, n[ 2 ]! ], turn ), [ 1, 0, 0, 1, -n[ 1 ]!, -n[ 2 ]! ] )
          : turn;
        break;
      }

      case 'skewX':
        step = [ 1, 0, Math.tan( rad( n[ 0 ] ?? 0 ) ), 1, 0, 0 ];
        break;

      case 'skewY':
        step = [ 1, Math.tan( rad( n[ 0 ] ?? 0 ) ), 0, 1, 0, 0 ];
        break;
    }

    out = multiply( out, step );
  }

  return out;
}

/* ---------------------------------------------------------------
 * Reading path data
 * ------------------------------------------------------------ */

/** Every command, with its numbers, in the order they were written. */
function tokens( d: string ): Array<{ kind: string; nums: number[] }> {
  const out: Array<{ kind: string; nums: number[] }> = [];

  for ( const match of d.matchAll( /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g ) ) {
    out.push( {
      kind: match[ 1 ] ?? 'M',
      nums: ( match[ 2 ] ?? '' ).match( /-?\d*\.?\d+(?:[eE][-+]?\d+)?/g )?.map( Number ) ?? [],
    } );
  }

  return out;
}

/**
 * Path data as subpaths of cubic segments, in the path's own coordinates.
 *
 * @param d The `d` attribute, in any of SVG's spellings.
 */
export function parsePath( d: string ): SubPath[] {
  const paths: SubPath[] = [];

  let current: SubPath | null = null as SubPath | null;
  let at: Point = { x: 0, y: 0 };
  let startedAt: Point = { x: 0, y: 0 };

  // What the shorthand curves reflect: the last control point, or the point
  // itself when the last command was not a curve of that kind.
  let lastCubic = null as Point | null;
  let lastQuad = null as Point | null;

  const line = ( to: Point ) => {
    const path = current;

    if ( ! path ) return;

    path.segments.push( { c1: { ...at }, c2: { ...to }, to: { ...to } } );
    at = to;
    lastCubic = null;
    lastQuad = null;
  };

  const cubic = ( c1: Point, c2: Point, to: Point ) => {
    const path = current;

    if ( ! path ) return;

    path.segments.push( { c1, c2, to } );
    at = to;
    lastCubic = c2;
    lastQuad = null;
  };

  const quad = ( control: Point, to: Point ) => {
    // A quadratic is a cubic whose controls sit two thirds of the way along.
    cubic(
      { x: at.x + ( 2 / 3 ) * ( control.x - at.x ), y: at.y + ( 2 / 3 ) * ( control.y - at.y ) },
      { x: to.x + ( 2 / 3 ) * ( control.x - to.x ), y: to.y + ( 2 / 3 ) * ( control.y - to.y ) },
      to
    );

    lastQuad = control;
  };

  const begin = ( to: Point ) => {
    const path = current;

    if ( path && path.segments.length ) paths.push( path );

    current = { start: { ...to }, segments: [], closed: false };
    at = to;
    startedAt = { ...to };
    lastCubic = null;
    lastQuad = null;
  };

  for ( const token of tokens( d ) ) {
    const relative = token.kind === token.kind.toLowerCase();
    const kind = token.kind.toUpperCase();
    const n = token.nums;
    const rx = ( value: number ) => ( relative ? at.x + value : value );
    const ry = ( value: number ) => ( relative ? at.y + value : value );

    if ( kind === 'Z' ) {
      const path = current;

      if ( path ) {
        path.closed = true;
        paths.push( path );
        current = null;
      }

      at = { ...startedAt };
      continue;
    }

    // Extra number groups repeat the command, which is how SVG writes a run of
    // line segments or curves without saying L or C every time.
    const step = { M: 2, L: 2, H: 1, V: 1, C: 6, S: 4, Q: 4, T: 2, A: 7 }[ kind ] ?? 2;

    for ( let i = 0; i + step <= n.length; i += step ) {
      const v = n.slice( i, i + step );

      switch ( kind ) {
        case 'M':
          // A second pair after a moveto is a lineto, as SVG defines it.
          if ( i === 0 ) begin( { x: rx( v[ 0 ]! ), y: ry( v[ 1 ]! ) } );
          else line( { x: rx( v[ 0 ]! ), y: ry( v[ 1 ]! ) } );
          break;

        case 'L':
          line( { x: rx( v[ 0 ]! ), y: ry( v[ 1 ]! ) } );
          break;

        case 'H':
          line( { x: rx( v[ 0 ]! ), y: at.y } );
          break;

        case 'V':
          line( { x: at.x, y: ry( v[ 0 ]! ) } );
          break;

        case 'C':
          cubic(
            { x: rx( v[ 0 ]! ), y: ry( v[ 1 ]! ) },
            { x: rx( v[ 2 ]! ), y: ry( v[ 3 ]! ) },
            { x: rx( v[ 4 ]! ), y: ry( v[ 5 ]! ) }
          );
          break;

        case 'S': {
          const mirror = lastCubic ? { x: 2 * at.x - lastCubic.x, y: 2 * at.y - lastCubic.y } : { ...at };

          cubic( mirror, { x: rx( v[ 0 ]! ), y: ry( v[ 1 ]! ) }, { x: rx( v[ 2 ]! ), y: ry( v[ 3 ]! ) } );
          break;
        }

        case 'Q':
          quad( { x: rx( v[ 0 ]! ), y: ry( v[ 1 ]! ) }, { x: rx( v[ 2 ]! ), y: ry( v[ 3 ]! ) } );
          break;

        case 'T': {
          const mirror = lastQuad ? { x: 2 * at.x - lastQuad.x, y: 2 * at.y - lastQuad.y } : { ...at };

          quad( mirror, { x: rx( v[ 0 ]! ), y: ry( v[ 1 ]! ) } );
          break;
        }

        case 'A': {
          const to = { x: rx( v[ 5 ]! ), y: ry( v[ 6 ]! ) };

          for ( const piece of arcToCubics( at, v[ 0 ]!, v[ 1 ]!, v[ 2 ]!, !! v[ 3 ], !! v[ 4 ], to ) ) {
            cubic( piece.c1, piece.c2, piece.to );
          }

          break;
        }
      }
    }
  }

  const last = current as SubPath | null;

  if ( last && last.segments.length ) paths.push( last );

  return paths;
}

/**
 * An elliptical arc as cubic segments.
 *
 * The endpoint parameterisation SVG uses, turned into the centre one, then cut
 * into pieces of at most a quarter turn - the point past which a cubic stops
 * being a good enough circle.
 */
function arcToCubics(
  from: Point,
  rx: number,
  ry: number,
  rotation: number,
  large: boolean,
  sweep: boolean,
  to: Point
): Segment[] {
  if ( rx === 0 || ry === 0 ) return [ { c1: { ...from }, c2: { ...to }, to: { ...to } } ];

  const angle = ( rotation * Math.PI ) / 180;
  const cos = Math.cos( angle );
  const sin = Math.sin( angle );

  let a = Math.abs( rx );
  let b = Math.abs( ry );

  const dx = ( from.x - to.x ) / 2;
  const dy = ( from.y - to.y ) / 2;
  const x1 = cos * dx + sin * dy;
  const y1 = -sin * dx + cos * dy;

  // An arc too small to reach is grown until it just does, as SVG requires.
  const lambda = ( x1 * x1 ) / ( a * a ) + ( y1 * y1 ) / ( b * b );

  if ( lambda > 1 ) {
    a *= Math.sqrt( lambda );
    b *= Math.sqrt( lambda );
  }

  const sign = large === sweep ? -1 : 1;
  const top = a * a * b * b - a * a * y1 * y1 - b * b * x1 * x1;
  const bottom = a * a * y1 * y1 + b * b * x1 * x1;
  const factor = sign * Math.sqrt( Math.max( 0, top / ( bottom || 1 ) ) );

  const cx1 = ( factor * a * y1 ) / b;
  const cy1 = ( -factor * b * x1 ) / a;
  const cx = cos * cx1 - sin * cy1 + ( from.x + to.x ) / 2;
  const cy = sin * cx1 + cos * cy1 + ( from.y + to.y ) / 2;

  const angleOf = ( x: number, y: number ) => Math.atan2( ( y - cy1 ) / b, ( x - cx1 ) / a );

  const start = angleOf( x1, y1 );
  let sweepAngle = angleOf( -x1, -y1 ) - start;

  if ( ! sweep && sweepAngle > 0 ) sweepAngle -= 2 * Math.PI;
  if ( sweep && sweepAngle < 0 ) sweepAngle += 2 * Math.PI;

  const pieces = Math.max( 1, Math.ceil( Math.abs( sweepAngle ) / ( Math.PI / 2 ) ) );
  const each = sweepAngle / pieces;
  const handle = ( 4 / 3 ) * Math.tan( each / 4 );

  const at = ( theta: number ): Point => ( {
    x: cos * a * Math.cos( theta ) - sin * b * Math.sin( theta ) + cx,
    y: sin * a * Math.cos( theta ) + cos * b * Math.sin( theta ) + cy,
  } );

  const slope = ( theta: number ): Point => ( {
    x: -cos * a * Math.sin( theta ) - sin * b * Math.cos( theta ),
    y: -sin * a * Math.sin( theta ) + cos * b * Math.cos( theta ),
  } );

  const out: Segment[] = [];

  for ( let i = 0; i < pieces; i++ ) {
    const t1 = start + i * each;
    const t2 = t1 + each;
    const p1 = at( t1 );
    const p2 = at( t2 );
    const d1 = slope( t1 );
    const d2 = slope( t2 );

    out.push( {
      c1: { x: p1.x + handle * d1.x, y: p1.y + handle * d1.y },
      c2: { x: p2.x - handle * d2.x, y: p2.y - handle * d2.y },
      to: p2,
    } );
  }

  return out;
}

/* ---------------------------------------------------------------
 * Moving and writing them out
 * ------------------------------------------------------------ */

export function transformSubPaths( paths: SubPath[], m: Matrix ): SubPath[] {
  return paths.map( ( path ) => ( {
    start: apply( m, path.start ),
    closed: path.closed,
    segments: path.segments.map( ( segment ) => ( {
      c1: apply( m, segment.c1 ),
      c2: apply( m, segment.c2 ),
      to: apply( m, segment.to ),
    } ) ),
  } ) );
}

/** What the whole thing covers, or null when there is nothing in it. */
export function bounds( paths: SubPath[] ): { x: number; y: number; w: number; h: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const see = ( point: Point ) => {
    minX = Math.min( minX, point.x );
    minY = Math.min( minY, point.y );
    maxX = Math.max( maxX, point.x );
    maxY = Math.max( maxY, point.y );
  };

  for ( const path of paths ) {
    see( path.start );

    // The control points too: a curve never leaves their hull, so this is
    // never too small - only, at worst, a little generous.
    for ( const segment of path.segments ) {
      see( segment.c1 );
      see( segment.c2 );
      see( segment.to );
    }
  }

  if ( ! isFinite( minX ) || ! isFinite( minY ) ) return null;

  return { x: minX, y: minY, w: Math.max( 0, maxX - minX ), h: Math.max( 0, maxY - minY ) };
}

/** The subset everything downstream reads: absolute M, L, C and Z. */
export function toPathData( paths: SubPath[], places = 4 ): string {
  const n = ( value: number ) => String( Math.round( value * 10 ** places ) / 10 ** places );
  const out: string[] = [];

  for ( const path of paths ) {
    out.push( `M ${ n( path.start.x ) } ${ n( path.start.y ) }` );

    let at = path.start;

    for ( const segment of path.segments ) {
      const straight =
        Math.abs( segment.c1.x - at.x ) < 1e-6 &&
        Math.abs( segment.c1.y - at.y ) < 1e-6 &&
        Math.abs( segment.c2.x - segment.to.x ) < 1e-6 &&
        Math.abs( segment.c2.y - segment.to.y ) < 1e-6;

      out.push(
        straight
          ? `L ${ n( segment.to.x ) } ${ n( segment.to.y ) }`
          : `C ${ n( segment.c1.x ) } ${ n( segment.c1.y ) } ${ n( segment.c2.x ) } ${ n( segment.c2.y ) } ${ n( segment.to.x ) } ${ n( segment.to.y ) }`
      );

      at = segment.to;
    }

    if ( path.closed ) out.push( 'Z' );
  }

  return out.join( ' ' );
}
